<template>
  <div class="table-playground">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row mb-4">
        <div class="col-12">
          <h2 class="text-primary">
            <i class="fas fa-table mr-2"></i>
            Schema-Driven Table Playground
          </h2>
          <p class="lead text-muted">
            Interactive demonstration of the SchemaTable component with various configurations
          </p>
        </div>
      </div>

      <!-- Controls -->
      <div class="row mb-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">
                <i class="fas fa-cogs mr-2"></i>
                Schema Selection
              </h5>
            </div>
            <div class="card-body">
              <div class="form-group">
                <label for="schema-select">Choose Example Schema:</label>
                <select
                  id="schema-select"
                  v-model="selectedSchemaKey"
                  class="form-control"
                  @change="onSchemaChange"
                >
                  <option
                    v-for="(schema, key) in availableSchemas"
                    :key="key"
                    :value="key"
                  >
                    {{ schema.title }}
                  </option>
                </select>
              </div>
              
              <div class="form-group">
                <small class="form-text text-muted">
                  {{ currentSchema.description }}
                </small>
              </div>

              <div class="btn-group btn-group-toggle w-100" data-toggle="buttons">
                <label 
                  class="btn btn-outline-primary"
                  :class="{ active: showSchema }"
                >
                  <input 
                    v-model="showSchema"
                    type="checkbox"
                    autocomplete="off"
                  > 
                  Show Schema JSON
                </label>
                <label 
                  class="btn btn-outline-info"
                  :class="{ active: showData }"
                >
                  <input 
                    v-model="showData"
                    type="checkbox"
                    autocomplete="off"
                  > 
                  Show Sample Data
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">
                <i class="fas fa-sliders-h mr-2"></i>
                Display Options
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <label for="rows-per-page">Rows per page:</label>
                    <select 
                      id="rows-per-page"
                      v-model="tableOptions.perPage"
                      class="form-control"
                    >
                      <option :value="5">5</option>
                      <option :value="10">10</option>
                      <option :value="25">25</option>
                      <option :value="50">50</option>
                    </select>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <div class="form-check mt-4">
                      <input
                        id="loading-state"
                        v-model="tableOptions.loading"
                        class="form-check-input"
                        type="checkbox"
                      >
                      <label class="form-check-label" for="loading-state">
                        Show Loading State
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="form-group">
                <label class="form-label">Table Style:</label>
                <div class="btn-group-toggle" data-toggle="buttons">
                  <label class="btn btn-sm btn-outline-secondary mr-1">
                    <input 
                      v-model="tableOptions.striped"
                      type="checkbox"
                      autocomplete="off"
                    > Striped
                  </label>
                  <label class="btn btn-sm btn-outline-secondary mr-1">
                    <input 
                      v-model="tableOptions.hover"
                      type="checkbox"
                      autocomplete="off"
                    > Hover
                  </label>
                  <label class="btn btn-sm btn-outline-secondary">
                    <input 
                      v-model="tableOptions.bordered"
                      type="checkbox"
                      autocomplete="off"
                    > Bordered
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Schema and Data Display -->
      <div class="row mb-4" v-if="showSchema || showData">
        <div class="col-md-6" v-if="showSchema">
          <div class="card">
            <div class="card-header">
              <h6 class="card-title mb-0">
                <i class="fas fa-code mr-2"></i>
                Schema Configuration
              </h6>
            </div>
            <div class="card-body">
              <pre class="schema-json"><code>{{ formattedSchema }}</code></pre>
            </div>
          </div>
        </div>
        
        <div class="col-md-6" v-if="showData">
          <div class="card">
            <div class="card-header">
              <h6 class="card-title mb-0">
                <i class="fas fa-database mr-2"></i>
                Sample Data ({{ currentData.length }} records)
              </h6>
            </div>
            <div class="card-body">
              <pre class="data-json"><code>{{ formattedData }}</code></pre>
            </div>
          </div>
        </div>
      </div>

      <!-- Table Display -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">
                <i class="fas fa-table mr-2"></i>
                {{ currentSchema.title }} Example
              </h5>
              <div class="btn-group">
                <button 
                  class="btn btn-outline-secondary btn-sm"
                  @click="regenerateData"
                >
                  <i class="fas fa-sync mr-1"></i>
                  Refresh Data
                </button>
                <button
                  class="btn btn-outline-info btn-sm"
                  @click="exportSchema"
                >
                  <i class="fas fa-download mr-1"></i>
                  Export Schema
                </button>
              </div>
            </div>
            <div class="card-body table-container">
              <div class="table-wrapper">
                <SchemaTable
                  :schema="computedSchema"
                  :data="currentData"
                  :loading="tableOptions.loading"
                  @action-click="handleTableAction"
                  @sort-changed="handleSortChange"
                  @row-click="handleRowClick"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Event Log -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="card-title mb-0">
                <i class="fas fa-list mr-2"></i>
                Event Log
              </h6>
              <button 
                class="btn btn-outline-secondary btn-sm"
                @click="clearEventLog"
              >
                Clear Log
              </button>
            </div>
            <div class="card-body">
              <div class="event-log">
                <div
                  v-for="(event, index) in eventLog"
                  :key="`event-${index}`"
                  class="event-entry p-2 border-left border-primary mb-2"
                >
                  <strong class="text-primary">{{ event.type }}</strong>
                  <small class="text-muted float-right">{{ event.timestamp }}</small>
                  <pre class="mt-1 mb-0"><code>{{ event.data }}</code></pre>
                </div>
                
                <div v-if="eventLog.length === 0" class="text-muted text-center py-3">
                  No events captured yet. Interact with the table to see events here.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SchemaTable from '@/components/common/SchemaTable.vue'

export default {
  name: 'TablePlayground',
  
  components: {
    SchemaTable
  },
  
  data() {
    return {
      selectedSchemaKey: 'bills',
      
      showSchema: false,
      showData: false,
      
      tableOptions: {
        perPage: 10,
        loading: false,
        striped: true,
        hover: true,
        bordered: false
      },
      
      eventLog: [],
      
      // Generated data cache
      dataCache: {}
    }
  },
  
  computed: {
    currentSchema() {
      return this.availableSchemas[this.selectedSchemaKey]
    },
    
    currentData() {
      return this.dataCache[this.selectedSchemaKey] || []
    },
    
    computedSchema() {
      return {
        ...this.currentSchema.schema,
        options: {
          ...this.currentSchema.schema.options,
          perPage: this.tableOptions.perPage,
          striped: this.tableOptions.striped,
          hover: this.tableOptions.hover,
          bordered: this.tableOptions.bordered
        }
      }
    },
    
    formattedSchema() {
      return JSON.stringify(this.currentSchema.schema, null, 2)
    },
    
    formattedData() {
      return JSON.stringify(this.currentData.slice(0, 3), null, 2) + 
             (this.currentData.length > 3 ? `\n... and ${this.currentData.length - 3} more records` : '')
    },
    
    availableSchemas() {
      return {
        bills: {
          title: 'Billing System (Bills) - Pixel Width Demo',
          description: 'Customer bills demonstrating fixed pixel width control for consistent column sizing. The Status column header shows a `?` help tooltip (headerHelp) — hover it for the status legend.',
          schema: {
            columns: [
              {
                key: 'billNumber',
                label: 'Bill #',
                dataType: 'string',
                sortable: true,
                width: '160px'
              },
              {
                key: 'customerName',
                label: 'Customer Name',
                dataType: 'string',
                sortable: true,
                width: '220px'
              },
              {
                key: 'billDate',
                label: 'Bill Date',
                dataType: 'date',
                formatter: { type: 'date', options: { format: 'short' } },
                sortable: true,
                width: '110px'
              },
              {
                key: 'amount',
                label: 'Amount',
                dataType: 'currency',
                formatter: { type: 'currency', options: { currency: 'GBP' } },
                align: 'right',
                sortable: true,
                width: '140px'
              },
              {
                key: 'status',
                label: 'Status',
                dataType: 'string',
                icon: {
                  status: true,
                  default: 'fas fa-question-circle'
                },
                showText: true,
                sortable: true,
                width: '100px',
                headerHelpTitle: 'Bill Status',
                headerHelp: `
                  <div><b>Paid</b> - Settled in full</div>
                  <div><b>Pending</b> - Awaiting payment</div>
                  <div><b>Overdue</b> - Past the due date</div>
                  <div><b>Approved</b> - Reviewed and approved</div>
                `
              },
              {
                key: 'isPaid',
                label: 'Paid',
                dataType: 'boolean',
                booleanStyle: 'check',
                align: 'center',
                width: '80px'
              }
            ],
            actions: [
              {
                name: 'view',
                label: 'View',
                icon: 'fas fa-eye',
                class: 'btn-info'
              },
              {
                name: 'approve',
                label: 'Approve',
                icon: 'fas fa-check',
                class: 'btn-success',
                disabled: (item) => item.status === 'approved'
              },
              {
                name: 'delete',
                label: 'Delete',
                icon: 'fas fa-trash',
                class: 'btn-danger',
                disabled: (item) => item.isPaid
              }
            ],
            options: {
              striped: true,
              hover: true,
              pagination: true,
              perPage: 10
            }
          },
          sampleDataGenerator: this.generateBillsData
        },
        
        transactions: {
          title: 'Financial Transactions - Percentage Width Demo',
          description: 'Payment transactions demonstrating percentage-based width distribution for responsive design',
          schema: {
            columns: [
              {
                key: 'transactionId',
                label: 'Transaction IDxx',
                dataType: 'string',
                formatter: { type: 'text', options: { maxLength: 12 } },
                width: '150px'
              },
              {
                key: 'type',
                label: 'Type',
                dataType: 'string',
                icon: {
                  mapping: {
                    'credit': 'fas fa-plus-circle text-success',
                    'debit': 'fas fa-minus-circle text-danger',
                    'transfer': 'fas fa-exchange-alt text-info'
                  },
                  default: 'fas fa-question-circle'
                },
                width: '10%'
              },
              {
                key: 'amount',
                label: 'Amount',
                dataType: 'currency',
                formatter: { type: 'currency' },
                align: 'right',
                sortable: true,
                width: '100px'
              },
              {
                key: 'processingFee',
                label: 'Fee %',
                dataType: 'percentage',
                formatter: { type: 'percentage', options: { decimals: 1 } },
                align: 'right',
                width: '100px'
              },
              {
                key: 'created',
                label: 'Date/Time',
                dataType: 'date',
                formatter: { type: 'date', options: { format: 'datetime' } },
                sortable: true,
                width: '150px'
              },
              {
                key: 'isProcessed',
                label: 'Processed',
                dataType: 'boolean',
                booleanStyle: 'toggle',
                width: '10%'
              },
              {
                key: 'description', 
                label: 'Transaction Notes',
                dataType: 'string',
                formatter: { type: 'text', options: { maxLength: 50 } },
                width: '10%'
              }
            ],
            actions: [
              {
                name: 'details',
                label: 'Details',
                icon: 'fas fa-info-circle',
                class: 'btn-primary'
              },
              {
                name: 'reverse',
                label: 'Reverse',
                icon: 'fas fa-undo',
                class: 'btn-secondary',
                disabled: (item) => !item.isProcessed
              }
            ]
          },
          sampleDataGenerator: this.generateTransactionsData
        },
        
        charges: {
          title: 'Customer Charges - Fill Column Demo',
          description: 'Service charges demonstrating fill column (100%) behavior alongside fixed width columns',
          schema: {
            columns: [
              {
                key: 'category',
                label: 'Category',
                dataType: 'string',
                icon: {
                  mapping: {
                    'gas': 'fas fa-fire text-danger',
                    'electricity': 'fas fa-bolt text-warning',
                    'water': 'fas fa-tint text-primary',
                    'service': 'fas fa-cog text-secondary'
                  }
                },
                width: '120px'
              },
              {
                key: 'description',
                label: 'Service Description',
                dataType: 'string',
                formatter: { type: 'text', options: { maxLength: 80 } },
                sortable: true,
                width: '100%'
              },
              {
                key: 'unitPrice',
                label: 'Unit Price',
                dataType: 'currency',
                formatter: { type: 'currency' },
                align: 'right',
                width: '120px'
              },
              {
                key: 'quantity',
                label: 'Qty',
                dataType: 'number',
                formatter: { type: 'number', options: { decimals: 2 } },
                align: 'right',
                width: '80px'
              },
              {
                key: 'totalAmount',
                label: 'Total',
                dataType: 'currency',
                formatter: { type: 'currency' },
                align: 'right',
                sortable: true,
                width: '120px'
              },
              {
                key: 'isRecurring',
                label: 'Recurring',
                dataType: 'boolean',
                booleanStyle: 'square',
                width: '90px'
              }
            ],
            actions: [
              {
                name: 'edit',
                label: 'Edit',
                icon: 'fas fa-edit',
                class: 'btn-primary'
              },
              {
                name: 'clone',
                label: 'Clone',
                icon: 'fas fa-copy',
                class: 'btn-secondary'
              }
            ]
          },
          sampleDataGenerator: this.generateChargesData
        }
      }
    }
  },
  
  mounted() {
    this.generateAllData()
  },
  
  methods: {
    onSchemaChange() {
      // Ensure data is generated for the new schema
      if (!this.dataCache[this.selectedSchemaKey]) {
        this.regenerateData()
      }
    },
    
    generateAllData() {
      Object.keys(this.availableSchemas).forEach(key => {
        this.dataCache[key] = this.availableSchemas[key].sampleDataGenerator(15)
      })
    },
    
    regenerateData() {
      const generator = this.currentSchema.sampleDataGenerator
      this.$set(this.dataCache, this.selectedSchemaKey, generator(15))
    },
    
    generateBillsData(count = 10) {
      const statuses = ['draft', 'pending', 'approved', 'paid', 'overdue']
      const customers = ['Acme Corp', 'Tech Solutions Ltd', 'Green Energy Co', 'City Council', 'Royal Hospital']
      
      return Array.from({ length: count }, (_, i) => ({
        billNumber: `INV-${String(2024001 + i).padStart(7, '0')}`,
        customerName: customers[Math.floor(Math.random() * customers.length)],
        billDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000),
        amount: Math.round((Math.random() * 2000 + 100) * 100) / 100,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        isPaid: Math.random() > 0.6
      }))
    },
    
    generateTransactionsData(count = 10) {
      const types = ['credit', 'debit', 'transfer']
      const descriptions = [
        'Customer payment received',
        'Bill payment processed',
        'Refund issued to customer',
        'Service charge collection',
        'Account transfer completed',
        'Direct debit payment',
        'Standing order payment',
        'Manual adjustment entry',
        'System correction',
        'Fee collection processed'
      ]
      
      return Array.from({ length: count }, (_, i) => ({
        transactionId: `TXN${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        type: types[Math.floor(Math.random() * types.length)],
        amount: Math.round((Math.random() * 1000 + 50) * 100) / 100,
        processingFee: Math.round(Math.random() * 5 * 100) / 100,
        created: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        isProcessed: Math.random() > 0.3,
        description: descriptions[Math.floor(Math.random() * descriptions.length)]
      }))
    },
    
    generateChargesData(count = 10) {
      const categories = ['gas', 'electricity', 'water', 'service']
      const services = [
        'Monthly Gas Supply - Standard Rate Consumption',
        'Electricity Usage - Peak Hours Premium Rate',
        'Water & Sewerage - Combined Service with Environmental Levy',
        'Customer Service Charge - Account Management and Support',
        'Standing Charge - Daily Connection Fee for Network Maintenance',
        'Meter Reading Service - Quarterly Physical Inspection',
        'Connection Fee - New Service Installation and Configuration',
        'Maintenance Fee - Annual Equipment Servicing and Calibration',
        'Emergency Callout - Out of Hours Technical Support Response',
        'Data Processing Charge - Monthly Usage Analytics and Reporting'
      ]
      
      return Array.from({ length: count }, (_, i) => ({
        description: services[Math.floor(Math.random() * services.length)],
        category: categories[Math.floor(Math.random() * categories.length)],
        unitPrice: Math.round((Math.random() * 50 + 5) * 100) / 100,
        quantity: Math.round((Math.random() * 100 + 1) * 100) / 100,
        get totalAmount() {
          return Math.round(this.unitPrice * this.quantity * 100) / 100
        },
        isRecurring: Math.random() > 0.4
      }))
    },
    
    handleTableAction(event) {
      this.logEvent('Action Click', event)
      
      // Demo action handling
      this.$snotify.info(`Action "${event.action}" clicked for item at index ${event.index}`)
    },
    
    handleSortChange(event) {
      this.logEvent('Sort Changed', event)
    },
    
    handleRowClick(event) {
      this.logEvent('Row Click', {
        index: event.index,
        item: event.item
      })
    },
    
    logEvent(type, data) {
      this.eventLog.unshift({
        type,
        data: JSON.stringify(data, null, 2),
        timestamp: new Date().toLocaleTimeString()
      })
      
      // Keep only last 10 events
      if (this.eventLog.length > 10) {
        this.eventLog = this.eventLog.slice(0, 10)
      }
    },
    
    clearEventLog() {
      this.eventLog = []
    },
    
    exportSchema() {
      const dataStr = JSON.stringify(this.computedSchema, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = `schema-${this.selectedSchemaKey}.json`
      link.click()
      
      this.$snotify.success('Schema exported successfully!')
    }
  }
}
</script>

<style scoped>
.table-playground {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 20px 0;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.schema-json,
.data-json {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 1rem;
  font-size: 0.875rem;
  max-height: 400px;
  overflow-y: auto;
}

.event-log {
  max-height: 300px;
  overflow-y: auto;
}

.event-entry {
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}

.event-entry pre {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 0.8rem;
}

.btn-group-toggle .btn {
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
}

.btn-group-toggle .btn:last-child {
  margin-right: 0;
}

/* Custom form styling */
.form-check-input:checked {
  background-color: #007bff;
  border-color: #007bff;
}

/* Adjust card spacing */
.card-body {
  padding: 1.25rem;
}

/* Adjust card body padding to accommodate table */
.card-body.table-container {
  padding: 1.25rem 10px !important; /* Maintain vertical padding, reduce horizontal */
  overflow-x: auto;
  box-sizing: border-box;
}

/* Table wrapper to constrain inner content */
.table-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}

/* Ensure tables within the container respect boundaries */
.table-container .table {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  margin-bottom: 0;
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

/* Make the playground responsive */
@media (max-width: 768px) {
  .table-playground {
    padding: 10px 0;
  }
  
  .schema-json,
  .data-json {
    font-size: 0.75rem;
    max-height: 200px;
  }
}